"use client";

import Navigation from "@/components/Navigation";
import { useMonarchMetadata } from "@/context/MonarchMetadataContext";
import { isTablet, onResize } from "@/helpers/browser";
import cn from "@/lib/cn";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import "@/styles/swiper-shared.css";
import React, { useCallback, useEffect, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";
import { SwiperOptions } from "swiper/types";
import type { BentoTabProps } from "../../types";
import BentoCard from "../BentoCard/BentoCard";
import styles from "./BentoTab.module.css";

function syncSlideAccessibility(
  swiper: SwiperType,
  visibleSlideIndices: Set<number>
) {
  swiper.slides.forEach((slide, index) => {
    if (visibleSlideIndices.has(index)) {
      slide.removeAttribute("inert");
      slide.removeAttribute("aria-hidden");
      return;
    }

    slide.setAttribute("inert", "");
    slide.setAttribute("aria-hidden", "true");
  });
}

const BentoTab: React.FC<BentoTabProps> = ({
  cards,
  id,
  layoutType,
  footer,
  onButtonClick,
  gridClass,
  enableCarousel = true,
  carouselBreakpoint = 1024,
  alwaysCarousel = false,
  slidesPerViewByBreakpoint,
  tabType,
  sliderClassName,
  enableContentZoom,
  sliderOverflowStyle,
}) => {
  const [isTabletView, setIsTabletView] = React.useState(false);
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [, setBreakpointChanged] = useState(0);
  const [visibleSlideIndices, setVisibleSlideIndices] = useState<Set<number>>(
    new Set()
  );
  const monarchMetadata = useMonarchMetadata();

  // Backward compatible: use prop if provided, otherwise fall back to existing logic
  const shouldEnableZoom = enableContentZoom ?? id === "mobile";

  const getCarouselBreakpoint = useCallback(() => {
    return 1039;
  }, []);

  useEffect(() => {
    const effectiveBreakpoint = getCarouselBreakpoint();
    const checkIsTablet = () => {
      setIsTabletView(isTablet(effectiveBreakpoint));
    };

    checkIsTablet();
    const removeResizeListener = onResize(checkIsTablet);
    return removeResizeListener;
  }, [carouselBreakpoint, tabType, getCarouselBreakpoint]);

  // Calculate which slides should be fully visible based on activeIndex and slidesPerView
  useEffect(() => {
    if (!swiper) return;

    const updateVisibleSlides = () => {
      const slidesPerView = (swiper.params.slidesPerView as number) || 1;
      const currentIndex = swiper.activeIndex;
      const indices = new Set<number>();

      for (let i = 0; i < Math.floor(slidesPerView); i++) {
        indices.add(currentIndex + i);
      }

      setVisibleSlideIndices(indices);
    };

    updateVisibleSlides();
    swiper.on("slideChange", updateVisibleSlides);
    swiper.on("breakpoint", updateVisibleSlides);

    return () => {
      swiper.off("slideChange", updateVisibleSlides);
      swiper.off("breakpoint", updateVisibleSlides);
    };
  }, [swiper]);

  useEffect(() => {
    if (!swiper) return;
    syncSlideAccessibility(swiper, visibleSlideIndices);
  }, [swiper, visibleSlideIndices]);

  const getBreakpoints = (): { [width: number]: SwiperOptions } => {
    if (slidesPerViewByBreakpoint) {
      return Object.fromEntries(
        Object.entries(slidesPerViewByBreakpoint).map(
          ([width, slidesPerView]) => [
            Number(width),
            { slidesPerView, centeredSlides: false, spaceBetween: 24 },
          ]
        )
      );
    }

    if (tabType === "entertainment") {
      return {
        768: {
          slidesPerView: 2,
          centeredSlides: false,
          spaceBetween: 24,
        },
        1040: {
          slidesPerView: 3,
          centeredSlides: false,
          spaceBetween: 24,
        },
      };
    }
    if (tabType === "mobile") {
      return {
        783: {
          slidesPerView: 2,
          centeredSlides: false,
          spaceBetween: 24,
        },
        1040: {
          slidesPerView: 4,
          centeredSlides: false,
          spaceBetween: 24,
        },
      };
    }

    return {
      847: {
        slidesPerView: 2,
        centeredSlides: false,
        spaceBetween: 24,
      },
    };
  };

  // For entertainment tab, always show carousel. For others, only show on tablet/mobile
  const shouldShowCarousel =
    enableCarousel &&
    (alwaysCarousel ||
      tabType === "entertainment" ||
      tabType === "mobile" ||
      isTabletView);

  if (shouldShowCarousel) {
    const slidesPerView = (swiper?.params?.slidesPerView as number) || 1;
    // Ceil so a fractional slidesPerView (a peeking next card) still gets a
    // bullet for its final snap position.
    const totalPositions = Math.max(
      1,
      Math.ceil(cards.length - slidesPerView + 1)
    );
    const shouldShowPagination = Boolean(swiper) && totalPositions > 1;
    return (
      <>
        <div
          className={cn(
            styles.bentoCarouselContainer,
            // Collapses with the pagination row's own top margin, so the gap
            // below the cards resolves to 24px without an !important override.
            shouldShowPagination && "mb-6",
            sliderClassName
          )}
          key={tabType}
        >
          <div className="relative">
            <Swiper
              modules={[]}
              spaceBetween={24}
              slidesPerView={1}
              slidesPerGroup={1}
              centeredSlides={true}
              loop={false}
              navigation={false}
              watchSlidesProgress={true}
              onSwiper={(swiperInstance) => {
                setSwiper(swiperInstance);
                // Listen for breakpoint changes to update pagination
                swiperInstance.on("breakpoint", () => {
                  setBreakpointChanged((prev) => prev + 1);
                });
              }}
              onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
              breakpoints={getBreakpoints()}
              className={styles.bentoSwiper}
            >
              {cards.map((card, index) => (
                <SwiperSlide
                  key={id + "-" + index}
                  className={cn({
                    "faded-overflow": sliderOverflowStyle === "fade",
                    "visible-overflow": sliderOverflowStyle === "visible",
                    "hidden-overflow": sliderOverflowStyle === "hide",
                    "fully-in-view": visibleSlideIndices.has(index),
                  })}
                  onFocusCapture={() => {
                    if (!visibleSlideIndices.has(index)) {
                      swiper?.slideTo(index);
                    }
                  }}
                >
                  <BentoCard
                    {...card}
                    className={cn(card.className, "w-full h-full")}
                    onButtonClick={onButtonClick || card.onButtonClick}
                    enableContentZoom={shouldEnableZoom}
                    tagularProps={{
                      position: index + 1,
                      ...(monarchMetadata && { monarchMetadata }),
                    }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            {shouldShowPagination ? (
              <Navigation
                onPrevious={() => swiper?.slidePrev()}
                onNext={() => swiper?.slideNext()}
                isPreviousDisabled={activeIndex === 0}
                isNextDisabled={activeIndex >= cards.length - slidesPerView}
                tagularProps={{
                  location: "bento",
                  ...(monarchMetadata && { monarchMetadata }),
                }}
              />
            ) : null}
          </div>
        </div>

        {shouldShowPagination ? (
          <div className="pagination-container">
            {Array.from({ length: totalPositions }, (_, i) => {
              const isActive = activeIndex === i;
              return (
                <button
                  key={i}
                  className={`pagination-bullet ${
                    isActive ? "pagination-bullet-active" : ""
                  }`}
                  onClick={() => {
                    swiper?.slideTo(i);
                    fireElementClicked({
                      elementType: "iconButton",
                      text: `Pagination Dot ${i + 1}`,
                      name: `iconButtonBentoTabPaginationDot${i + 1}`,
                      location: "bento",
                      position: i + 1,
                      ...(monarchMetadata && { monarchMetadata }),
                    });
                  }}
                  aria-label={`Go to slide ${i + 1}`}
                  tabIndex={0}
                />
              );
            })}
          </div>
        ) : null}
        {footer ? (
          <div className={cn(!shouldShowPagination ? "mt-6" : "")}>
            {footer}
          </div>
        ) : null}
      </>
    );
  }

  const getLayoutContainerClass = () => {
    return cn(
      "grid !grid-cols-1 sm:!grid-cols-12 gap-4 pb-6 px-0 py-4 [grid-template-areas:unset] [grid-template-columns:repeat(1,minmax(0,1fr))] sm:[grid-template-columns:repeat(12,minmax(0,1fr))]",
      gridClass
    );
  };

  const getCardGridProps = (
    cardIndex: number
  ): { gridColumn?: string; gridRow?: string } => {
    if (!layoutType || layoutType === "custom") {
      return {};
    }

    const gridPropsMap: Record<
      string,
      Array<{ gridColumn?: string; gridRow?: string }>
    > = {
      "3-cards-wide-top": [
        { gridColumn: "1 / -1", gridRow: "1" },
        { gridColumn: "1 / 7", gridRow: "2" },
        { gridColumn: "7 / 13", gridRow: "2" },
      ],
      "3-cards-wide-bottom": [
        { gridColumn: "1 / 7", gridRow: "1" },
        { gridColumn: "7 / 13", gridRow: "1" },
        { gridColumn: "span 12", gridRow: "2" },
      ],
      "3-cards-tall-left": [
        { gridColumn: "1 / 9", gridRow: "span 2" },
        { gridColumn: "9 / 13", gridRow: "1" },
        { gridColumn: "9 / 13", gridRow: "2" },
      ],
      "3-cards-tall-right": [
        { gridColumn: "1 / 5", gridRow: "1" },
        { gridColumn: "1 / 5", gridRow: "2" },
        { gridColumn: "5 / 13", gridRow: "span 2" },
      ],
      "3-cards-equal": [
        { gridColumn: "1 / 5", gridRow: "1" },
        { gridColumn: "5 / 9", gridRow: "1" },
        { gridColumn: "9 / 13", gridRow: "1" },
      ],
      "4-cards-grid": [
        { gridColumn: "1 / 7", gridRow: "1" },
        { gridColumn: "7 / 13", gridRow: "1" },
        { gridColumn: "1 / 7", gridRow: "2" },
        { gridColumn: "7 / 13", gridRow: "2" },
      ],
      "4-cards-tall-left": [
        { gridColumn: "1 / 7", gridRow: "span 3" },
        { gridColumn: "7 / 13", gridRow: "1" },
        { gridColumn: "7 / 13", gridRow: "2" },
        { gridColumn: "7 / 13", gridRow: "3" },
      ],
      "4-cards-tall-right": [
        { gridColumn: "1 / 7", gridRow: "1" },
        { gridColumn: "1 / 7", gridRow: "2" },
        { gridColumn: "1 / 7", gridRow: "3" },
        { gridColumn: "7 / 13", gridRow: "span 3" },
      ],
      "4-cards-mixed": [
        { gridColumn: "span 12", gridRow: "1" },
        { gridColumn: "1 / 7", gridRow: "2" },
        { gridColumn: "7 / 13", gridRow: "2" },
        { gridColumn: "span 12", gridRow: "3" },
      ],
      "4-cards-equal": [
        { gridColumn: "1 / 4", gridRow: "1" },
        { gridColumn: "4 / 7", gridRow: "1" },
        { gridColumn: "7 / 10", gridRow: "1" },
        { gridColumn: "10 / 13", gridRow: "1" },
      ],
      "5-cards": [
        { gridColumn: "span 12", gridRow: "1" },
        { gridColumn: "1 / 7", gridRow: "2" },
        { gridColumn: "7 / 13", gridRow: "2" },
        { gridColumn: "1 / 7", gridRow: "3" },
        { gridColumn: "7 / 13", gridRow: "3" },
      ],
    };

    return gridPropsMap[layoutType]?.[cardIndex] || {};
  };

  return (
    <>
      <div className={getLayoutContainerClass()}>
        {cards.map((card, index) => {
          const gridProps = getCardGridProps(index);
          return (
            <BentoCard
              key={id + "-" + index}
              {...card}
              gridColumn={gridProps.gridColumn || card.gridColumn}
              gridRow={gridProps.gridRow || card.gridRow}
              className={card.className}
              onButtonClick={onButtonClick || card.onButtonClick}
              enableContentZoom={shouldEnableZoom}
              tagularProps={{
                position: index + 1,
                ...(monarchMetadata && { monarchMetadata }),
              }}
            />
          );
        })}
      </div>
      {footer ? footer : null}
    </>
  );
};

export default BentoTab;
